import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { ConditionReportPhoto, ConditionReportRole, getErrorMessage } from '../types';
import { useTranslation, Lang } from '../i18n';
import { formatDateTime } from '../formatDate';

interface Props {
  orderId: number;
  side: number;
  role: ConditionReportRole;
  photos: ConditionReportPhoto[];
  canUpload: boolean;
  lang: Lang;
  onUploaded: () => void;
  onError: (msg: string) => void;
}

/** Photos are private, so they are fetched with the auth header and shown from blob URLs. */
const PhotoThumb: React.FC<{ orderId: number; photo: ConditionReportPhoto; caption: string }> = ({ orderId, photo, caption }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    api.get(`/orders/${orderId}/reports/photos/${photo.id}`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { /* a missing thumbnail is shown as an empty box */ });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orderId, photo.id]);

  return (
    <figure style={{ margin: 0, width: 96 }}>
      <button
        type="button"
        onClick={() => url && window.open(url, '_blank', 'noopener')}
        style={{ padding: 0, border: '1px solid var(--color-border, #ddd)', borderRadius: 6, width: 96, height: 72, overflow: 'hidden', background: 'var(--color-surface, #fafafa)', cursor: url ? 'zoom-in' : 'default' }}
        aria-label={caption}
      >
        {url && <img src={url} alt={caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      </button>
      <figcaption className="text-muted" style={{ fontSize: '0.7rem', lineHeight: 1.2, marginTop: 2 }}>{caption}</figcaption>
    </figure>
  );
};

export const ConditionReportPhotos: React.FC<Props> = ({
  orderId, side, role, photos, canUpload, lang, onUploaded, onError,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    onError('');
    try {
      // One photo per request keeps each request well under the proxy's body size limit.
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('photos', file);
        await api.post(`/orders/${orderId}/reports/photos?side=${side}`, form);
      }
    } catch (err) {
      onError(getErrorMessage(err, t('reports.photos_upload_error')));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded();
    }
  };

  const who = (r: ConditionReportRole) => (r === role ? t('reports.you') : t('reports.other_party'));

  return (
    <div style={{ margin: '0.5rem 0' }}>
      {photos.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {photos.map(p => (
            <PhotoThumb
              key={p.id}
              orderId={orderId}
              photo={p}
              caption={`${who(p.uploadedByRole)} · ${formatDateTime(p.uploadedAt, lang)}`}
            />
          ))}
        </div>
      ) : (
        <p className="text-muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('reports.photos_none')}</p>
      )}
      {canUpload && (
        <div style={{ marginTop: '0.4rem' }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => upload(e.target.files)}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? t('reports.photos_uploading') : t('reports.photos_add')}
          </button>
        </div>
      )}
    </div>
  );
};
